import { components } from '../.generated';

/**
 * The One API's canonical error shape. Every Graph query/command result carries
 * an optional `error: GraphError`, and `GraphError.code` is a stable, typed enum
 * (`E_NOT_FOUND`, `E_ACCESS_DENIED`, ...) — far more useful to callers than a raw
 * HTTP status or free-text message. This is the core error model for OneService.
 */
export type GraphError = components['schemas']['GraphError'];
export type GraphCode = components['schemas']['GraphCode'];

/**
 * The error every OneService query/command rejects with. It wraps a `GraphError`
 * so callers can branch on a stable `.code` regardless of which transport
 * (HTTP or SignalR) produced it, while `Error.message`/stack stay ergonomic.
 */
export class OneServiceError extends Error {
  readonly code: GraphCode;
  readonly graphError: GraphError;
  readonly details?: { [key: string]: string };
  /** Nested errors (multi-operation requests / validation failures). */
  readonly errors?: GraphError[];

  constructor(graphError: GraphError, options?: { cause?: unknown }) {
    super(graphError.message || graphError.code, options);
    this.name = 'OneServiceError';
    this.graphError = graphError;
    this.code = graphError.code;
    this.details = graphError.details;
    this.errors = graphError.errors;
  }
}

/** Map a legacy HTTP status (or a missing one, i.e. network error) to a GraphCode. */
export function httpStatusToGraphCode(status?: number): GraphCode {
  switch (status) {
    case 400:
      return 'E_BAD_REQUEST';
    case 401:
    case 403:
      return 'E_ACCESS_DENIED';
    case 404:
      return 'E_NOT_FOUND';
    case 408:
      return 'E_TIMEOUT';
    case 409:
      return 'E_CONFLICT';
    case 422:
      return 'E_UNPROCESSABLE_CONTENT';
    case 429:
      return 'E_TOO_MANY_REQUESTS';
    case 501:
      return 'E_NOT_SUPPORTED';
    default:
      if (status === undefined) return 'E_NO_CONNECTION'; // network / no response
      if (status >= 500) return 'E_SERVICE_ERROR';
      return 'E_UNKNOWN';
  }
}

/**
 * A non-Graph failure — a legacy HTTP status, a network error, or a SignalR hub
 * exception — wrapped so it "looks and smells like" a GraphError (mapped `.code`
 * via {@link httpStatusToGraphCode}). Keeps the original `status`/`body` for
 * debugging. This is the bridge that lets callers treat every OneService error
 * uniformly through `.code`, even for endpoints that don't speak GraphError yet.
 */
export class LegacyError extends OneServiceError {
  readonly status?: number;
  readonly body?: unknown;

  constructor(params: {
    status?: number;
    message?: string;
    body?: unknown;
    cause?: unknown;
  }) {
    const code = httpStatusToGraphCode(params.status);
    const message =
      params.message ||
      pickMessage(params.body) ||
      (params.status ? `Request failed (${params.status})` : 'Network error');
    super({ code, message }, { cause: params.cause });
    this.name = 'LegacyError';
    this.status = params.status;
    this.body = params.body;
  }
}

/** Pull a human message out of a legacy body (RFC7807 Problem or ad-hoc JSON). */
function pickMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  for (const key of ['detail', 'title', 'message', 'error']) {
    const value = b[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}

/**
 * If a Graph result carries an `error`, throw it as a {@link OneServiceError};
 * otherwise return the result unchanged. Both transports funnel results through
 * this so a `result.error` (which the backend returns inside a 200) surfaces as
 * a rejection rather than a silent, easy-to-miss field.
 */
export function throwIfGraphError<R>(result: R): R {
  const error = (result as { error?: GraphError } | null | undefined)?.error;
  if (error) throw new OneServiceError(error);
  return result;
}
